import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MagnifyingGlass, Trash, PencilSimple, Globe } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "", domain: "", target_language: "", target_country: "", description: "", seo_keywords: "" });
  const [saving, setSaving] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      const [langRes, countryRes] = await Promise.all([
        axios.get(`${API}/languages`, { withCredentials: true }),
        axios.get(`${API}/countries`, { withCredentials: true }),
      ]);
      setLanguages(langRes.data);
      setCountries(countryRes.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchProjects(); fetchMeta(); }, [fetchProjects, fetchMeta]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/projects`, form, { withCredentials: true });
      toast.success("Project created");
      setDialogOpen(false);
      setForm({ name: "", domain: "", target_language: "", target_country: "", description: "", seo_keywords: "" });
      fetchProjects();
    } catch (err) {
      toast.error("Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project and all its content?")) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
      toast.success("Project deleted");
      fetchProjects();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.domain.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="projects-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Projects</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Manage your content projects</p>
        </div>
        <Button
          data-testid="create-project-btn"
          onClick={() => setDialogOpen(true)}
          className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-9 px-4 text-sm font-semibold"
        >
          <Plus size={16} className="mr-1.5" /> New Project
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
        <Input
          data-testid="projects-search"
          placeholder="Search projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-[#141417] border-[#2A2A2E] text-white placeholder:text-[#555] rounded-sm h-9"
        />
      </div>

      {/* Projects Table */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#2A2A2E]">
              <th className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-5 py-3">Name</th>
              <th className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-5 py-3 hidden md:table-cell">Domain</th>
              <th className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-5 py-3 hidden lg:table-cell">Language</th>
              <th className="text-left text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-5 py-3 hidden lg:table-cell">Country</th>
              <th className="text-center text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-5 py-3">Contents</th>
              <th className="text-center text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-5 py-3">Status</th>
              <th className="text-right text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2A2A2E]">
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-[#555]">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-[#555]">No projects found</td></tr>
            ) : filtered.map((p, i) => (
              <tr
                key={p.id}
                data-testid={`project-row-${p.id}`}
                className={`hover:bg-[#1A1A1E] transition-colors duration-200 cursor-pointer animate-fade-up stagger-${Math.min(i+1, 4)}`}
                onClick={() => navigate(`/projects/${p.id}`)}
              >
                <td className="px-5 py-3">
                  <div className="text-sm text-white font-medium">{p.name}</div>
                  <div className="text-xs text-[#A1A1AA] md:hidden">{p.domain}</div>
                </td>
                <td className="px-5 py-3 text-sm text-[#A1A1AA] hidden md:table-cell">{p.domain}</td>
                <td className="px-5 py-3 text-sm text-[#A1A1AA] hidden lg:table-cell">{p.target_language}</td>
                <td className="px-5 py-3 text-sm text-[#A1A1AA] hidden lg:table-cell">{p.target_country}</td>
                <td className="px-5 py-3 text-sm text-white text-center font-bold">{p.contents_count || 0}</td>
                <td className="px-5 py-3 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${p.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-[#2A2A2E] text-[#A1A1AA]'}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    <button data-testid={`edit-project-${p.id}`} className="p-1.5 text-[#A1A1AA] hover:text-[#4361EE] transition-colors" onClick={() => navigate(`/projects/${p.id}`)}>
                      <PencilSimple size={14} />
                    </button>
                    <button data-testid={`delete-project-${p.id}`} className="p-1.5 text-[#A1A1AA] hover:text-[#E53935] transition-colors" onClick={() => handleDelete(p.id)}>
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141417] border-[#2A2A2E] text-white max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Globe size={20} className="inline mr-2 text-[#4361EE]" />
              New Project
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Project Name</label>
              <Input
                data-testid="project-name-input"
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10"
                required
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Domain</label>
              <Input
                data-testid="project-domain-input"
                value={form.domain}
                onChange={e => setForm({...form, domain: e.target.value})}
                placeholder="example.com"
                className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Target Language</label>
                <Select value={form.target_language} onValueChange={v => setForm({...form, target_language: v})}>
                  <SelectTrigger data-testid="project-language-select" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141417] border-[#2A2A2E] text-white">
                    {languages.map(l => (
                      <SelectItem key={l.code} value={l.name} className="hover:bg-[#1E1E22]">{l.name} ({l.native})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Target Country</label>
                <Select value={form.target_country} onValueChange={v => setForm({...form, target_country: v})}>
                  <SelectTrigger data-testid="project-country-select" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141417] border-[#2A2A2E] text-white">
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.name} className="hover:bg-[#1E1E22]">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Description</label>
              <Input
                data-testid="project-desc-input"
                value={form.description}
                onChange={e => setForm({...form, description: e.target.value})}
                className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">SEO Keywords (for AI article)</label>
              <Input
                data-testid="project-seo-keywords-input"
                value={form.seo_keywords}
                onChange={e => setForm({...form, seo_keywords: e.target.value})}
                placeholder="e.g. streaming, HD videos, best content"
                className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#A1A1AA] hover:text-white rounded-sm">Cancel</Button>
              <Button
                data-testid="project-submit-btn"
                type="submit"
                disabled={saving}
                className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm px-6 font-semibold"
              >
                {saving ? "Creating..." : "Create Project"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
